import { ReactNode, useContext, useEffect, useState, useRef } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { Trans } from 'react-i18next';
import classNames from 'classnames';

import { i18n as I18n, withTranslation } from 'utils/i18n';

import Page from 'components/page';
import SearchMap from 'components/searchMap';
import VenueSearchForm from 'components/forms/venueSearch';
import Favourites from 'components/favourites';
import FavouriteSpace from 'components/favouriteSpace';
import FavouriteVenue from 'components/favouriteVenue';
import SortDropDown, { SortOption } from 'components/sortDropDown';
import MapVenue from 'components/searchMap/mapVenue';
import Switch from 'components/switch';
import Pager from 'components/pager';

import { AuthContext } from 'state/auth';
import { FavouritesContext } from 'state/favourites';
import { initialState as emptyState } from 'state/search/reducer';

import useStoredBooking from 'hooks/useStoredBooking';
import { Venue } from 'hooks/useVenue';
import useVenueSearch, { Filters } from 'hooks/useVenueSearch';
import useIsMobile from 'hooks/useIsMobile';
import { sortFields } from 'constants/sortFields';

type View = 'list' | 'map';

interface Props {
  pageSize?: number;
  initialPage?: number;
  initialView?: View;
  initialFilters?: Filters;
}

export const defaultFilters: Filters = {
  distance: null,
  sort: 'Name',
  location: '',
  latLng: null,
};

const PropertiesPage: NextPage<Props> = ({
  pageSize = 10,
  initialPage = 1,
  initialView = 'map',
  initialFilters = defaultFilters,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [currentView, setCurrentView] = useState<View>(initialView);

  const [filters, setFilters] = useState<Filters>(initialFilters);
  const { location, latLng, distance = '', sort = '' } = filters;

  const { loggedIn } = useContext(AuthContext);
  const {
    actions: { fetchFavourites = () => {}, removeFavourite = () => {} } = {},
    venues: favouriteVenues = [],
    spaces: favouriteSpaces = [],
  } = useContext(FavouritesContext);

  const displayLocation = latLng ? I18n.t('shared.current_location') : location;
  useEffect(() => {
    if (loggedIn) {
      fetchFavourites('venues');
      fetchFavourites('spaces');
    }
  }, [fetchFavourites, loggedIn]);

  const {
    data: {
      venues = [],
      alternativeVenues = [],
      totalVenues = 0,
      unfilteredTotalVenues = 0,
    } = {},
    loading: venuesLoading,
  } = useVenueSearch({
    filters,
    viewType: currentView,
    page: currentPage,
    perPage: pageSize,
  });

  const mapRef = useRef(null);

  const [selectedVenue, setSelectedVenue] = useState<Venue | undefined>();

  const selectVenue = venue => {
    setCurrentView('map');
    setSelectedVenue(venue);
  };

  const search = ({
    location: nextLocation,
    distance: nextDistance,
    geolocation,
  }) => {
    setFilters(prev => ({
      ...prev,
      location: nextLocation,
      distance: nextDistance,
      latLng: geolocation,
    }));
    setCurrentPage(1);
  };

  const router = useRouter();

  const [
    { spaceIds: storedSpaceIds, venueId: storedVenueId },
    setStoredBooking,
  ] = useStoredBooking();

  const selectSpace = (space, venue) => {
    if (storedSpaceIds.length === 0 || storedVenueId === venue.id)
      setStoredBooking(storedBooking => ({
        ...storedBooking,
        venueId: venue.id,
        spaceIds: [...storedSpaceIds, space.id],
      }));

    router.push(`/rooms/${space.id}`);
  };

  const toggleView = toggled => {
    const nextView = toggled ? 'list' : 'map';
    setCurrentView(nextView);
    setCurrentPage(1);
    if (nextView !== 'map') setSelectedVenue();
  };

  const isMobile = useIsMobile();

  const searchResults = venues.length > 0 ? venues : alternativeVenues;

  const currentPageData: ReactNode = searchResults.map(venue => (
    <li key={venue.id}>
      <MapVenue
        className="venues__list--card"
        venue={venue}
        location={displayLocation}
        showRooms
        showFeatures
        showBookRoomsButton
        showMapOnThumbNail
        floorplansButtonPosition="image"
        onClickMap={() => selectVenue(venue)}
        organisationName={venue.organisation.name}
      />
    </li>
  ));

  const handlePageChange = event => {
    event.preventDefault();
    setCurrentPage(page => page + 1);
  };

  const formBlockClasses = classNames('block', 'block--with-border', {
    'block--no-margin': isMobile,
  });
  const classForControls = classNames('layout__first__top', {
    'layout__first__top--map-view': currentView === 'map',
  });
  const renderSearchForm = () => (
    <div className={formBlockClasses}>
      <div className="block__inner">
        <div className="block__title">
          <h3 className="block__title-heading">
            {I18n.t('venue_search_form.heading')}
          </h3>
        </div>
        <VenueSearchForm
          initialValues={{
            location,
            distance: distance || '',
            geolocation: latLng,
          }}
          onSubmit={search}
          onReset={mapRef.current?.resetMap}
          enableReset={!isMobile}
        />
      </div>
    </div>
  );

  const renderSubHeading = () => {
    if (currentView === 'map') {
      if (totalVenues === unfilteredTotalVenues)
        return <p>{I18n.t('showing_properties.showing_all_properties')}</p>;

      const ResetLink = ({ children, ...props }) => (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            setFilters(emptyState.filters);
          }}
          title={I18n.t('showing_properties.see_all_properties')}
          {...props}
        >
          {children}
        </a>
      );

      return (
        <p>
          <Trans
            i18nKey="showing_properties.total_properties_found"
            i18n={I18n}
            components={[<ResetLink />]}
            count={totalVenues}
            values={{ total: unfilteredTotalVenues }}
          />
        </p>
      );
    }

    return (
      <p>
        {I18n.t(
          totalVenues > pageSize
            ? 'showing_properties.showing_properties_long'
            : 'showing_properties.showing_properties_short',
          { count: searchResults.length, total: totalVenues }
        )}
      </p>
    );
  };

  const sortOptions = Object.keys(sortFields);
  const unavailableSortOptions = location === '' ? ['Distance'] : [];
  const availableSortOptions = sortOptions.filter(
    option => !unavailableSortOptions.includes(option as SortOption)
  );

  const changeSortBy = sort => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, sort }));
  };
  const noResultsMessage = () => {
    if (!displayLocation) {
      return I18n.t('search_results.alternative_venues.no_matches');
    }

    if (alternativeVenues.length > 0) {
      return (
        <>
          {I18n.t('search_results.alternative_venues.no_location_results')}
          <strong>{displayLocation}</strong>.{' '}
          {I18n.t('search_results.alternative_venues.nearest_properties')}
        </>
      );
    }

    return (
      <>
        {I18n.t('search_results.alternative_venues.no_location_results')}
        <strong>{displayLocation}</strong>.{' '}
        {I18n.t('search_results.alternative_venues.check_spelling')}
      </>
    );
  };

  return (
    <Page title={I18n.t('headers.properties_page.title')}>
      <main className="venues">
        <section>
          <div className="section-content">
            <h3>{I18n.t('headers.properties_page.heading')}</h3>
            {isMobile && renderSearchForm()}
            <div className="layout layout--two-col-stacked-left">
              <div className="layout__first">
                {!venuesLoading && venues.length === 0 && (
                  <>
                    <div className="venue-search__alternative_venues_message">
                      <p>{noResultsMessage()}</p>
                    </div>
                  </>
                )}
                <div className={classForControls}>
                  {!isMobile && renderSubHeading()}
                  <div className="layout__first__top--controls">
                    <Switch
                      className="venues__switch"
                      label={I18n.t('search_results.view_type.label')}
                      toggled={currentView === 'map'}
                      onSwitchChange={toggleView}
                    />

                    <SortDropDown
                      sortOptions={availableSortOptions}
                      onChange={e => changeSortBy(e.currentTarget.value)}
                      value={sort}
                      disabled={currentView === 'map'}
                    />
                  </div>
                </div>

                {currentView === 'map' && (
                  <>
                    <SearchMap
                      ref={mapRef}
                      venues={venues.length ? venues : alternativeVenues}
                      loading={venuesLoading}
                      selectedVenue={selectedVenue}
                      onSelectVenue={selectVenue}
                      className="our-properties-map"
                      location={displayLocation}
                      geolocation={latLng}
                      showRooms
                      showBookRoomsButton={!isMobile}
                      showFeatures={!isMobile}
                      floorplansButtonPosition={isMobile ? 'none' : 'image'}
                      imagePosition="top"
                    />
                    {isMobile && renderSubHeading()}
                  </>
                )}

                {currentView === 'list' && (
                  <>
                    {venuesLoading && (
                      <div className="search-map__container">
                        <i className="fa fa-spinner spinning" />
                      </div>
                    )}
                    {!venuesLoading && (
                      <ul data-testid="venue-list">{currentPageData}</ul>
                    )}
                    {!venuesLoading && searchResults.length > 0 && (
                      <Pager
                        className="venue-search--pager"
                        loading={venuesLoading}
                        currentPage={currentPage}
                        perPage={pageSize}
                        total={totalVenues}
                        onClick={handlePageChange}
                      />
                    )}
                  </>
                )}
              </div>
              <div className="layout__last">
                {!isMobile && renderSearchForm()}

                {loggedIn && (
                  <Favourites
                    title={I18n.t('favourite.venues.title')}
                    placeholder={I18n.t('favourite.venues.placeholder')}
                    showFavourites={!!favouriteVenues.length}
                  >
                    {favouriteVenues.map(venue => (
                      <FavouriteVenue
                        key={venue.id}
                        venue={venue}
                        onRemove={() => removeFavourite(venue.id, 'venues')}
                      />
                    ))}
                  </Favourites>
                )}

                {loggedIn && (
                  <Favourites
                    title={I18n.t('favourite.spaces.title')}
                    placeholder={I18n.t('favourite.spaces.placeholder')}
                    showFavourites={!!favouriteSpaces.length}
                  >
                    {favouriteSpaces.map(space => (
                      <FavouriteSpace
                        key={space.id}
                        space={space}
                        selectSpace={selectSpace}
                        onRemove={() => removeFavourite(space.id, 'spaces')}
                        navigateToSpace
                      />
                    ))}
                  </Favourites>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </Page>
  );
};

export const getInitialProps = () => {
  return {
    namespacesRequired: ['common'],
  };
};

export default withTranslation('common')(PropertiesPage);
